import React, { useState, useCallback } from 'react';
import moment from 'moment';
import styles from './AdminAnalyticsSection.module.scss';
import LoadingSpinner from '../Shared/LoadingSpinner.tsx';
import AnalyticsFiltersBar, { DatePreset } from './AnalyticsFiltersBar.tsx';
import AnalyticsKpiCard from './AnalyticsKpiCard.tsx';
import AnalyticsActiveUsersBlock from './AnalyticsActiveUsersBlock.tsx';
import AnalyticsReportTable from './AnalyticsReportTable.tsx';
import AnalyticsOverviewBarChart from './AnalyticsOverviewBarChart.tsx';
import AnalyticsSectionBarChart from './AnalyticsSectionBarChart.tsx';
import chartStyles from './AnalyticsCharts.module.scss';
import {
  AdminAnalyticsFilters,
  AnalyticsOverviewResponse,
  AnalyticsSection,
  ANALYTICS_SECTIONS,
  ANALYTICS_SECTION_LABELS,
} from '../../../../types/adminAnalytics.ts';
import {
  adminAnalyticsApi,
  triggerBlobDownload,
} from '../../../../services/adminAnalyticsApi.ts';
import {
  saveAdminAnalyticsPdf,
  ADMIN_ANALYTICS_EXPORT_ROOT_ID,
} from '../../../../utils/adminAnalyticsPdfExport.ts';
import {
  excludeActiveUserMetrics,
  splitUserMetrics,
} from '../../../../utils/adminAnalyticsActiveUsers.ts';

interface AdminAnalyticsSectionProps {
  overview: AnalyticsOverviewResponse | null;
  filters: AdminAnalyticsFilters;
  loading: boolean;
  error: string | null;
  onFiltersChange: (partial: Partial<AdminAnalyticsFilters>) => void;
  onFetch: () => void;
}

const AdminAnalyticsSection: React.FC<AdminAnalyticsSectionProps> = ({
  overview,
  filters,
  loading,
  error,
  onFiltersChange,
  onFetch,
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const applyPreset = useCallback(
    (preset: DatePreset) => {
      let from: moment.Moment;
      let to: moment.Moment = moment().endOf('day');

      switch (preset) {
        case 'last7':
          from = moment().subtract(6, 'days').startOf('day');
          break;
        case 'last30':
          from = moment().subtract(29, 'days').startOf('day');
          break;
        case 'thisMonth':
          from = moment().startOf('month');
          break;
        case 'lastMonth':
          from = moment().subtract(1, 'month').startOf('month');
          to = moment().subtract(1, 'month').endOf('month');
          break;
        default:
          from = moment().subtract(29, 'days').startOf('day');
      }

      onFiltersChange({
        from: from.toISOString(),
        to: to.toISOString(),
        useExplicitCompare: false,
        compareFrom: null,
        compareTo: null,
      });
    },
    [onFiltersChange]
  );

  const runExport = async (format: 'xlsx' | 'csv') => {
    setExportLoading(true);
    setExportError(null);
    try {
      const { blob, filename } = await adminAnalyticsApi.downloadExport(filters, format);
      triggerBlobDownload(blob, filename);
    } catch (err: any) {
      setExportError(err?.message ?? 'Error al exportar.');
    } finally {
      setExportLoading(false);
    }
  };

  const activeSection = filters.activeSection;
  const sectionsToShow: AnalyticsSection[] =
    activeSection === 'all'
      ? ANALYTICS_SECTIONS.filter((s) => overview?.sections[s]?.metrics?.length)
      : [activeSection];

  const handleExportPdf = async () => {
    if (!overview) return;
    setExportLoading(true);
    setExportError(null);
    try {
      await saveAdminAnalyticsPdf(
        overview,
        ADMIN_ANALYTICS_EXPORT_ROOT_ID,
        sectionsToShow
      );
    } catch (err: any) {
      setExportError(err?.message ?? 'Error al generar el PDF.');
    } finally {
      setExportLoading(false);
    }
  };

  const periodLabel = overview?.report?.meta?.periodLabel;
  const compareLabel = overview?.report?.meta?.comparePeriodLabel;

  return (
    <div className={styles.section}>
      <div className={styles.intro}>
        <h2 className={styles.title}>Estadísticas</h2>
        <p className={styles.subtitle}>
          Actividad registrada en el período seleccionado (no totales acumulados de la plataforma).
        </p>
      </div>

      <AnalyticsFiltersBar
        filters={filters}
        loading={loading}
        exportLoading={exportLoading}
        onFiltersChange={onFiltersChange}
        onApply={onFetch}
        onPreset={applyPreset}
        onExportXlsx={() => runExport('xlsx')}
        onExportCsv={() => runExport('csv')}
        onExportPdf={handleExportPdf}
      />

      {periodLabel && (
        <div className={styles.periodBanner}>
          <span>
            <strong>Período:</strong> {periodLabel}
          </span>
          {compareLabel && (
            <span>
              <strong>Comparación:</strong> {compareLabel}
            </span>
          )}
        </div>
      )}

      <div className={styles.sectionTabs}>
        <button
          type="button"
          className={`${styles.sectionTab} ${activeSection === 'all' ? styles.active : ''}`}
          onClick={() => onFiltersChange({ activeSection: 'all' })}
        >
          Todas
        </button>
        {ANALYTICS_SECTIONS.map((section) => (
          <button
            key={section}
            type="button"
            className={`${styles.sectionTab} ${activeSection === section ? styles.active : ''}`}
            onClick={() => onFiltersChange({ activeSection: section })}
          >
            {ANALYTICS_SECTION_LABELS[section]}
          </button>
        ))}
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {exportError && <div className={styles.error}>{exportError}</div>}

      {loading && <LoadingSpinner message="Cargando estadísticas..." />}

      {!loading && !error && overview && (
        <>
          <div
            id={ADMIN_ANALYTICS_EXPORT_ROOT_ID}
            className={chartStyles.exportCaptureRoot}
          >
            <div className={chartStyles.chartsBlock}>
              {activeSection === 'all' && sectionsToShow.length > 1 && (
                <AnalyticsOverviewBarChart
                  overview={overview}
                  sectionKeys={sectionsToShow}
                />
              )}
              {sectionsToShow.map((sectionKey) => {
                const block = overview.sections[sectionKey];
                const chartMetrics =
                  sectionKey === 'users'
                    ? excludeActiveUserMetrics(block?.metrics ?? [])
                    : block?.metrics ?? [];
                if (!chartMetrics.length) return null;
                return (
                  <AnalyticsSectionBarChart
                    key={`chart-${sectionKey}`}
                    exportId={sectionKey}
                    metrics={chartMetrics}
                    title={`${ANALYTICS_SECTION_LABELS[sectionKey]} — actual vs anterior`}
                  />
                );
              })}
            </div>
          </div>

          {sectionsToShow.map((sectionKey) => {
            const block = overview.sections[sectionKey];
            if (!block?.metrics?.length) return null;

            const { coreMetrics, activeMetrics } =
              sectionKey === 'users'
                ? splitUserMetrics(block.metrics)
                : { coreMetrics: block.metrics, activeMetrics: [] };

            return (
              <div key={sectionKey} className={styles.sectionBlock}>
                <h3 className={styles.sectionTitle}>
                  {ANALYTICS_SECTION_LABELS[sectionKey]}
                </h3>
                {coreMetrics.length > 0 && (
                  <div className={styles.kpiGrid}>
                    {coreMetrics.map((metric) => (
                      <AnalyticsKpiCard key={metric.key} metric={metric} />
                    ))}
                  </div>
                )}
                {activeMetrics.length > 0 && (
                  <AnalyticsActiveUsersBlock
                    metrics={activeMetrics}
                    filters={filters}
                    compareTo={overview.period.compareTo}
                  />
                )}
              </div>
            );
          })}

          <AnalyticsReportTable
            report={overview.report}
            compareTo={overview.period.compareTo}
          />
        </>
      )}

      {!loading && !error && !overview && (
        <p className={styles.empty}>Seleccioná un período y pulsá Actualizar para ver las métricas.</p>
      )}
    </div>
  );
};

export default AdminAnalyticsSection;
